import cx from 'classnames';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { ComponentProps, useMemo } from 'react';

import Trustpilot from '../../../modules/trustpilot';
import Breadcrumbs from '../../../modules/v2/breadcrumbs';
import ValueProposition from '../../../modules/value-proposition';

import BrandList from './brand-list';
import BrandTitle from './brand-title';
import BrandsSchema from './seo';
import LastEntries from '../../v2/vehicle/last-entries';
import SEOContent from './seo-content-section';

type TBrandsPage = {
  groups: Array<TCategory>;
  brands: { info: { total_products: number }; children: TBrand[] };
  total_products: number;
  selection: { category: TCategorySelection };
  hasSelection: ComponentProps<typeof BrandsSchema>['hasSelection'];
  partsInfo: ComponentProps<typeof BrandsSchema>['partsInfo'];
  breadcrumbs: ComponentProps<typeof BrandsSchema>['breadcrumbs'];
  stock?: { stock: number }[];
  translatedSlugs: any;
  lastEntries: TProductListItem[];
};

const BrandPage = ({
  groups,
  hasSelection,
  partsInfo,
  breadcrumbs,
  selection,
  brands,
  stock,
  lastEntries,
  translatedSlugs,
}: TBrandsPage) => {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const stockTotal = useMemo(
    () => (stock ? stock[0].stock : brands.info.total_products),
    [stock, brands]
  );

  return (
    <>
      <BrandsSchema
        hasSelection={hasSelection}
        partsInfo={partsInfo}
        breadcrumbs={breadcrumbs}
        translatedSlugs={translatedSlugs}
        count={stockTotal.toLocaleString(locale)}
        selection={selection}
      />
      <Breadcrumbs items={breadcrumbs} currentPage={t('common:brand.other')} />
      <BrandTitle
        hasSelection={hasSelection}
        groups={groups}
        selection={selection}
        info={partsInfo}
        total_products={stockTotal.toLocaleString(locale)}
      />
      <BrandList
        brands={brands.children}
        hasSelection={hasSelection}
        selection={selection}
      />
      {lastEntries && !!lastEntries.length && (
        <LastEntries products={lastEntries} />
      )}
      <ValueProposition />
      <SEOContent
        withPart={hasSelection}
        selection={selection}
        stock={stockTotal.toLocaleString(locale)}
        info={partsInfo}
      ></SEOContent>
      <div className={cx('py-8 lg:py-6')}>
        <div className={cx('container', 'mx-auto', 'text-center')}>
          <Trustpilot backgroundColor="bg-white" />
        </div>
      </div>
    </>
  );
};

export default BrandPage;
